import { useState, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Button from "../components/Button";
import { Input } from "../components/Input";
import { activateAccount } from "../services/api";
import { useTranslation } from "react-i18next";

export default function KycRegister() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { search } = useLocation();

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const hasToken = Boolean(token);

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");

    if (!hasToken) return setErr(t("kyc_activation.missing_token"));
    if (!password || password.length < 8)
      return setErr(t("kyc_activation.error_password"));
    if (password !== confirm) return setErr(t("kyc_activation.error_mismatch"));

    setLoading(true);
    try {
      await activateAccount({ token, password });
      // On success, redirect to login so user can sign in.
      nav("/login", { replace: true });
    } catch (e) {
      const serverMsg = e?.message || e?.error || "";
      if (serverMsg === "token_invalid" || serverMsg === "token_wrong_purpose") {
        setErr(t("kyc_activation.error_token_invalid"));
      } else if (serverMsg === "token_expired") {
        setErr(t("kyc_activation.error_token_expired"));
      } else if (serverMsg === "token_used") {
        setErr(t("kyc_activation.error_token_used"));
      } else if (serverMsg === "weak_password") {
        setErr(t("kyc_activation.error_weak_password"));
      } else {
        setErr(serverMsg || t("kyc_activation.error_generic"));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-gray-50">
      <div className="w-full max-w-md rounded-2xl border border-black/10 bg-white shadow-sm p-6 text-center">
        <h1 className="text-2xl font-bold mb-1">{t("kyc_activation.title")}</h1>
        <p className="text-sm text-black/60 mb-4">{t("kyc_activation.subtitle")}</p>

        {hasToken ? (
          <form onSubmit={onSubmit} className="space-y-3 text-left">
            <div>
              <label className="block text-sm font-medium mb-1">{t("kyc_activation.password_label")}</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t("kyc_activation.confirm_label")}</label>
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>

            {err && <div className="text-sm text-red-600">{err}</div>}

            <Button disabled={loading} className="w-full">
              {loading ? t("kyc_activation.activating") : t("kyc_activation.btn_activate")}
            </Button>
          </form>
        ) : (
          <div className="space-y-3 text-left">
            <div className="text-sm text-black/70 mb-4">{t("kyc_activation.missing_token")}</div>
            <div className="text-xs text-black/60">
              <Link to="/login" className="text-blue-600 hover:text-blue-800 underline">
                {t("kyc_activation.go_to_login")}
              </Link>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
